import React, { useCallback, useEffect, useRef } from 'react';
import { Drawer, Hidden } from '@material-ui/core';
import { makeStyles, ThemeProvider } from '@material-ui/styles';
import clsx from 'clsx';
import * as Actions from 'app/store/actions';
import NavbarLayout from './NavbarLayout';
import { useDispatch, useSelector } from 'react-redux';

const navbarWidth = 280;

// How long the pointer must REST on the folded menu before it opens. Long
// enough that crossing the strip on the way somewhere else never triggers it,
// short enough that deliberately reaching for the menu still feels immediate.
const NAV_HOVER_OPEN_DELAY_MS = 350;

const useStyles = makeStyles(theme => ({
	wrapper: {
		display: 'flex',
		flexDirection: 'column',
		zIndex: 4,
		[theme.breakpoints.up('lg')]: {
			width: navbarWidth,
			minWidth: navbarWidth
		}
	},
	wrapperFolded: {
		[theme.breakpoints.up('lg')]: {
			width: 64,
			minWidth: 64
		}
	},
	navbar: {
		display: 'flex',
		overflow: 'hidden',
		flexDirection: 'column',
		flex: '1 1 auto',
		width: navbarWidth,
		minWidth: navbarWidth,
		height: '100%',
		zIndex: 4,
		transition: theme.transitions.create(['width', 'min-width'], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.shorter
		}),
		boxShadow: theme.shadows[3]
	},
	left: {
		left: 0
	},
	right: {
		right: 0
	},
	folded: {
		position: 'absolute',
		width: 64,
		minWidth: 64,
		top: 0,
		bottom: 0
	},
	foldedAndOpened: {
		width: navbarWidth,
		minWidth: navbarWidth
	},
	navbarContent: {
		flex: '1 1 auto'
	},
	foldedAndClosed: {
		'& $navbarContent': {
			'& .logo-icon': {
				width: 32,
				height: 32
			},
			'& .logo-text': {
				opacity: 0
			},
			'& .react-badge': {
				opacity: 0
			},
			'& .list-item-text, & .arrow-icon, & .item-badge': {
				opacity: 0
			},
			'& .list-subheader .list-subheader-text': {
				opacity: 0
			},
			'& .list-subheader:before': {
				content: '""',
				display: 'block',
				position: 'absolute',
				minWidth: 16,
				borderTop: '2px solid',
				opacity: 0.2
			},
			'& .collapse-children': {
				display: 'none'
			},
			'& .user': {
				'& .username, & .email': {
					opacity: 0
				},
				'& .avatar': {
					width: 40,
					height: 40,
					top: 32,
					padding: 0
				}
			},
			'& .list-item.active': {
				marginLeft: 12,
				width: 40,
				padding: 12,
				borderRadius: 20,
				'&.square': {
					borderRadius: 0,
					marginLeft: 0,
					paddingLeft: 24,
					width: '100%'
				}
			}
		}
	}
}));

const NavbarWrapperLayout = () => {
	const dispatch = useDispatch();
	const config = useSelector(({ fuse }) => fuse.settings.current.layout.config);
	const navbarTheme = useSelector(({ fuse }) => fuse.settings.navbarTheme);
	const navbar = useSelector(({ fuse }) => fuse.navbar);
	const role = useSelector(({ auth }) => auth.user.role);

	const classes = useStyles();

	const folded = config.navbar.folded;
	const foldedAndClosed = folded && !navbar.foldedOpen;
	const foldedAndOpened = folded && navbar.foldedOpen;

	// HOVER INTENT. The folded menu used to spring open the instant the pointer
	// touched it, so merely CROSSING it opened it - and on the Documents page,
	// where the reader's hand travels to Collabora's menus on the far side of
	// the screen, it kept flying open uninvited and shoving the page sideways.
	//
	// Wait a beat first: a pointer that is passing through is gone well before
	// the delay elapses, while one that is actually heading for the menu stays.
	// Leaving cancels a pending open, so a transit never opens it at all.
	const openTimer = useRef(null);
	const cancelPendingOpen = useCallback(() => {
		if (openTimer.current) {
			clearTimeout(openTimer.current);
			openTimer.current = null;
		}
	}, []);
	// A timer that outlives the component would dispatch into a dead tree.
	useEffect(() => cancelPendingOpen, [cancelPendingOpen]);

	const onNavMouseEnter = useCallback(() => {
		if (!foldedAndClosed) return;
		cancelPendingOpen();
		openTimer.current = setTimeout(() => {
			openTimer.current = null;
			dispatch(Actions.navbarOpenFolded());
		}, NAV_HOVER_OPEN_DELAY_MS);
	}, [foldedAndClosed, cancelPendingOpen, dispatch]);

	const onNavMouseLeave = useCallback(() => {
		cancelPendingOpen();
		if (foldedAndOpened) dispatch(Actions.navbarCloseFolded());
	}, [foldedAndOpened, cancelPendingOpen, dispatch]);

	return (
		<ThemeProvider theme={navbarTheme}>
			{role.length !== 0 && (
				<nav id="fuse-navbar" aria-label="Main navigation" className={clsx(classes.wrapper, folded && classes.wrapperFolded)}>
					<Hidden mdDown>
						<div
							className={clsx(
								classes.navbar,
								classes[config.navbar.position],
								folded && classes.folded,
								foldedAndOpened && classes.foldedAndOpened,
								foldedAndClosed && classes.foldedAndClosed
							)}
							onMouseEnter={onNavMouseEnter}
							onMouseLeave={onNavMouseLeave}
							style={{ backgroundColor: '#2e3443ff' }}
						>
							<NavbarLayout className={classes.navbarContent} />
						</div>
					</Hidden>

					<Hidden lgUp>
						<Drawer
							anchor={config.navbar.position}
							variant="temporary"
							open={navbar.mobileOpen}
							classes={{
								paper: classes.navbar
							}}
							onClose={() => dispatch(Actions.navbarCloseMobile())}
							ModalProps={{
								keepMounted: true // Better open performance on mobile.
							}}
						>
							<NavbarLayout className={classes.navbarContent} />
						</Drawer>
					</Hidden>
				</nav>
			)}
		</ThemeProvider>
	);
};

export default NavbarWrapperLayout;
